import { useState, useRef, useCallback, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import ToolPageLayout from '../components/ToolPageLayout';
import { useToolGenerate } from '../hooks/useToolGenerate';
import { pickPhotoFromLibrary } from '@/utils/nativePhoto';
import { track } from '@/utils/tracker';
import PromptAIHelpButton from '@/pages/generate/components/PromptAIHelpButton';

export default function BgReplacePage() {
  const { t } = useTranslation();
  const [image, setImage] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    isGenerating,
    result,
    error,
    doGenerate,
    doRegenerate,
    doCancel,
    clearError,
    pointsCost,
    controls,
    expandToolPrompt,
  } = useToolGenerate('bg_replace');

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImage(ev.target?.result as string);
      track.referenceUploadSuccess({ source: 'tool_bg_replace', count: 1 });
    };
    reader.readAsDataURL(file);
  }, []);

  const canGenerate = !!image && prompt.trim().length > 0;

  const handleGenerate = () => {
    if (!canGenerate) return;
    doGenerate(prompt.trim(), image ?? undefined);
  };

  return (
    <ToolPageLayout
      title={t('extool_bgReplace')}
      icon="ri-image-edit-fill"
      iconBg="linear-gradient(135deg, #06B6D4, #22D3EE)"
      iconColor="#06B6D4"
      description={t('extool_bgReplace_desc')}
      guidance={{
        input: t('bg_replace_inputRequirement'),
        output: t('bg_replace_expectedResult'),
        fallback: t('bg_replace_fallbackTip'),
        beforeHint: t('bg_replace_beforeHint'),
        afterHint: t('bg_replace_afterHint'),
      }}
      pointsCost={pointsCost}
      onGenerate={handleGenerate}
      isGenerating={isGenerating}
      canGenerate={canGenerate}
      result={result}
      error={error}
      onRegenerate={doRegenerate}
      onCancel={doCancel}
      onClearError={clearError}
      controls={controls}
    >
      {/* Upload image */}
      <div
        className="rounded-3xl p-4 mb-3"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,107,157,0.12)',
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-5 h-5 rounded-md flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #06B6D4, #22D3EE)' }}
          >
            <i className="ri-image-add-line text-white text-xs" />
          </div>
          <span className="text-xs font-semibold text-gray-500">{t('bg_replace_uploadLabel')}</span>
          <span className="text-[10px] text-red-400 ml-auto">{t('gen_required')}</span>
        </div>

        {image ? (
          <div
            className="relative w-full rounded-2xl overflow-hidden mb-3"
            style={{ aspectRatio: '16/9', border: '1px solid rgba(255,107,157,0.15)' }}
          >
            <img src={image} alt="upload" className="w-full h-full object-contain" />
            <button
              onClick={() => setImage(null)}
              className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-gray-800/60 text-white cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => pickPhotoFromLibrary(fileInputRef, setImage)}
            className="w-full rounded-2xl flex flex-col items-center justify-center gap-2 cursor-pointer active:scale-[0.98] transition-transform"
            style={{
              aspectRatio: '16/9',
              background: 'rgba(6,182,212,0.08)',
              border: '2px dashed rgba(6,182,212,0.25)',
            }}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(6,182,212,0.12)' }}
            >
              <i className="ri-upload-cloud-2-line text-2xl text-cyan-400" />
            </div>
            <p className="text-sm text-gray-500">{t('bg_replace_uploadPlaceholder')}</p>
            <p className="text-xs text-gray-400">{t('img2img_supportFormat')}</p>
          </button>
        )}
      </div>

      {/* Background description */}
      <div
        className="rounded-3xl p-4 mb-3"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,107,157,0.12)',
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-5 h-5 rounded-md flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #06B6D4, #22D3EE)' }}
          >
            <i className="ri-pencil-line text-white text-xs" />
          </div>
          <span className="text-xs font-semibold text-gray-500">{t('bg_replace_promptLabel')}</span>
        </div>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={isGenerating}
          placeholder={t('bg_replace_promptPlaceholder')}
          className="w-full bg-transparent text-sm text-gray-700 placeholder-gray-300 resize-none outline-none leading-relaxed"
          rows={3}
          style={{ minHeight: '60px' }}
        />
        <div className="flex items-center justify-between mt-2">
          <PromptAIHelpButton
            profile={controls.aiHelpProfile}
            disabled={!prompt.trim() || isGenerating || controls.aiHelpDisabled}
            loading={controls.aiHelpLoading}
            onClick={() => {
              void expandToolPrompt(prompt)
                .then((expanded) => {
                  if (expanded != null) setPrompt(expanded);
                })
                .catch(() => undefined);
            }}
          />
          <span className="text-xs text-gray-300">{prompt.length}</span>
        </div>
        {controls.aiHelpError ? (
          <p className="mt-1 text-[11px] text-red-400" role="alert">
            {controls.aiHelpError}
          </p>
        ) : null}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </ToolPageLayout>
  );
}
